import React, { useState, useRef } from 'react';
import { publicAsset } from '../../utils/publicPath';

/**
 * SceneExplorer — First-person POV scene navigation.
 *
 * Props:
 *   step       — {
 *                  views: Array<{ id, label, asset, caption, exits: { forward?, back?, left?, right? } }>,
 *                  startView: string,
 *                  introCharacter?: string,
 *                  introDialogue?: string,
 *                  onComplete: string  (step key — handled by parent dispatcher)
 *                }
 *   onComplete — () => void — called when all views have been visited
 */

// Maps the symbolic asset names from level JSON to real image paths (Group D).
// scene-1: Operations Center  (Level 1)
// scene-2: Substation         (Level 4)
const ASSET_PATHS = {
  scene_ops_main_floor:       '/assets/group-d/scene-1/D1_ops_center_main.png',
  scene_ops_scada_wall:        '/assets/group-d/scene-1/D2_ops_center_scada.png',
  scene_ops_plc_room:          '/assets/group-d/scene-1/D3_ops_center_plc.png',
  scene_ops_hmi:               '/assets/group-d/scene-1/D4_ops_center_hmi.png',
  scene_sub_exterior:          '/assets/group-d/scene-2/D5_substation_view_exterior.png',
  scene_sub_control:           '/assets/group-d/scene-2/D6_substation_view_control_house.png',
  scene_sub_transformer:       '/assets/group-d/scene-2/D7_substation_view_transformer.png',
  substation_view_exterior:    '/assets/group-d/scene-2/D5_substation_view_exterior.png',
  substation_view_control_house: '/assets/group-d/scene-2/D6_substation_view_control_house.png',
  substation_view_transformer: '/assets/group-d/scene-2/D7_substation_view_transformer.png',
};

const ASSET_MAP = Object.fromEntries(
  Object.entries(ASSET_PATHS).map(([key, path]) => [key, publicAsset(path)])
);

const DIRECTION_LABELS = {
  forward: '↑ Forward',
  back:    '↓ Back',
  left:    '← Left',
  right:   '→ Right',
};

function SceneExplorer({ step, onComplete }) {
  const views = step?.views ?? [];
  const viewMap = Object.fromEntries(views.map((v) => [v.id, v]));

  const [currentId, setCurrentId] = useState(step?.startView ?? views[0]?.id ?? '');
  const [visited, setVisited] = useState(() => new Set([step?.startView ?? views[0]?.id ?? '']));
  const [transitioning, setTransitioning] = useState(false);
  const containerRef = useRef(null);

  const currentView = viewMap[currentId];

  const navigate = (targetId) => {
    if (transitioning || !viewMap[targetId]) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrentId(targetId);
      setVisited((prev) => {
        const next = new Set(prev);
        next.add(targetId);
        return next;
      });
      setTransitioning(false);
    }, 300);
  };

  if (!currentView) {
    return <div className="p-4 text-white/50">No scene data.</div>;
  }

  const imageSrc = ASSET_MAP[currentView.asset] ?? currentView.asset ?? null;
  const exits = currentView.exits ?? {};
  const allVisited = visited.size >= views.length;

  return (
    <div
      ref={containerRef}
      className="relative w-full flex flex-col"
      style={{ minHeight: '420px', background: '#0f172a' }}
    >
      {/* Scene image */}
      <div
        className="relative w-full overflow-hidden flex-1"
        style={{ minHeight: '320px', perspective: '800px' }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            opacity: transitioning ? 0 : 1,
            transform: transitioning ? 'translateZ(-40px)' : 'translateZ(0)',
          }}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={currentView.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                background: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                fontSize: 14,
              }}
            >
              {currentView.label}
            </div>
          )}
        </div>

        {/* Label overlay */}
        <div
          className="absolute top-0 left-0 right-0 px-4 py-2"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)', zIndex: 2 }}
        >
          <span className="text-white text-sm font-semibold">{currentView.label}</span>
        </div>

        {/* Visit progress */}
        <div className="absolute top-2 right-3 flex gap-1" style={{ zIndex: 2 }}>
          {views.map((v) => (
            <span
              key={v.id}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: visited.has(v.id) ? '#22d3ee' : '#334155',
                display: 'inline-block',
              }}
            />
          ))}
        </div>
      </div>

      {/* Caption */}
      <div
        className="px-4 py-3 text-sm text-slate-300"
        style={{ background: '#1e293b', minHeight: 56 }}
      >
        {currentView.caption}
      </div>

      {/* Navigation buttons */}
      <div
        className="flex items-center justify-center gap-3 px-4 py-3"
        style={{ background: '#0f172a', flexWrap: 'wrap' }}
      >
        {Object.entries(exits).map(([dir, targetId]) => (
          <button
            key={dir}
            onClick={() => navigate(targetId)}
            disabled={transitioning}
            className="px-4 py-2 text-sm font-medium rounded text-white"
            style={{
              background: '#1e3a5f',
              border: '1px solid #2563eb',
              cursor: transitioning ? 'not-allowed' : 'pointer',
              opacity: transitioning ? 0.5 : 1,
              minWidth: 100,
            }}
          >
            {DIRECTION_LABELS[dir] ?? dir}
            {visited.has(targetId) ? '' : ' •'}
          </button>
        ))}

        {allVisited && (
          <button
            onClick={onComplete}
            className="px-5 py-2 text-sm font-semibold rounded text-white"
            style={{ background: '#0e7490', border: '1px solid #22d3ee' }}
          >
            Continue →
          </button>
        )}
      </div>
    </div>
  );
}

export default SceneExplorer;
